import "./export.scss"

import {
  CDropdown,
  // CDropdownItem,
  CDropdownMenu,
  CDropdownToggle
} from "@coreui/react"
import Upload from "assets/icons/upload.png"
import * as FileSaver from "file-saver"
import PropTypes from "prop-types"
import React from "react"
import * as XLSX from "xlsx"
// import ReactExport from "react-export-excel"

// const ExcelFile = ReactExport.ExcelFile
// const ExcelSheet = ReactExport.ExcelFile.ExcelSheet

// const stringfyAllValues = (dataset) => {
//   dataset.data = dataset?.data?.map((eachData) =>
//     eachData?.map((eachValue) => ({
//       ...eachValue,
//       value: String(eachValue.value)
//     }))
//   )
//   return dataset
// }

export default function MultipleExport({
  children = <img src={Upload} alt="upload" style={{ height: "1.5rem" }} />,
  excelDatasets,
  fileName = "",
  isVulnerabilityTable = false,
  exportData
}) {
  function handleDownloadExcel(dataset, index) {
    try {
      console.log("Starting Excel export for dataset:", dataset?.name, "Index:", index)

      const { columns, data, name } = dataset

      // Validate dataset structure
      if (!columns || !Array.isArray(columns)) {
        console.error("Invalid columns in dataset:", columns)
        throw new Error("Excel export failed: Invalid columns structure")
      }

      if (!data || !Array.isArray(data)) {
        console.error("Invalid data in dataset:", data)
        throw new Error("Excel export failed: Invalid data structure")
      }

      if (!name || typeof name !== "string") {
        console.error("Invalid name in dataset:", name)
        throw new Error("Excel export failed: Invalid dataset name")
      }

      console.log(`Excel export validation: ${columns.length} columns, ${data.length} rows`)

      // Check for extremely large datasets that may cause memory issues
      if (data.length > 200000) {
        console.log("Extremely large dataset detected, using split file approach")
        return handleExtremelyLargeDataset(columns, data, name, index)
      }

      // Check if dataset is large (>25k rows) and handle accordingly
      const isLargeDataset = data.length > 25000
      if (isLargeDataset) {
        console.log("Large dataset detected, using memory-efficient processing approach")
        return handleLargeDatasetExport(columns, data, name, index)
      }

      // Standard processing for smaller datasets
      const alteredData = processDataRows(data)

      console.log(`Excel export data processed: ${alteredData.length} rows`)

      // Validate that we have valid data before creating the worksheet
      if (alteredData.length === 0) {
        console.warn("No data to export")
        alert("No data available to export")
        return
      }

      createAndDownloadExcel(columns, alteredData, name, index)

      console.log("Excel export completed successfully")
    } catch (error) {
      console.error("Excel export failed:", error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      alert(`Excel export failed: ${errorMessage}`)
    }
  }

  function processDataRows(data) {
    return data.map((x, rowIndex) => {
      if (!Array.isArray(x)) {
        console.error(`Row ${rowIndex} is not an array:`, x)
        throw new Error(`Excel export failed: Row ${rowIndex} is not an array`)
      }

      const alteredX = x.map((obj, colIndex) => {
        if (!obj || typeof obj !== "object") {
          console.warn(`Row ${rowIndex}, Column ${colIndex} is not an object:`, obj)
          return String(obj || "")
        }

        if (obj.value === undefined) {
          console.warn(`Row ${rowIndex}, Column ${colIndex} missing value property:`, obj)
          return String(obj.text || obj.value || "")
        }

        return String(obj.value || "")
      })
      return [...alteredX]
    })
  }

  function handleLargeDatasetExport(columns, data, name, index) {
    try {
      console.log(`Processing large dataset: ${data.length} rows`)

      // Show progress to user
      let progressTimeout

      const showProgress = (message) => {
        clearTimeout(progressTimeout)
        const progressDiv = document.getElementById("excel-export-progress") || createProgressDiv()
        progressDiv.textContent = message
        progressDiv.style.display = "block"
      }

      const hideProgress = () => {
        clearTimeout(progressTimeout)
        const progressDiv = document.getElementById("excel-export-progress")
        if (progressDiv) {
          progressDiv.style.display = "none"
        }
      }

      showProgress("Preparing large dataset export...")

      // Use smaller chunk size and more aggressive memory management
      const CHUNK_SIZE = 5000 // Reduced chunk size

      // Create workbook immediately to start streaming
      const wb = XLSX.utils.book_new()

      // Process data in very small chunks with immediate cleanup
      setTimeout(() => {
        try {
          let processedRows = 0
          const totalRows = data.length

          // Create worksheet with initial header
          let ws = XLSX.utils.aoa_to_sheet([columns])

          // Process data in chunks with memory cleanup
          const processChunk = (startIndex) => {
            if (startIndex >= totalRows) {
              // Finalize export
              finalizeExport()
              return
            }

            const endIndex = Math.min(startIndex + CHUNK_SIZE, totalRows)
            const chunk = data.slice(startIndex, endIndex)

            showProgress(`Processing ${endIndex} of ${totalRows} rows (${Math.round((endIndex / totalRows) * 100)}%)`)

            try {
              // Process chunk with minimal memory footprint
              const processedChunk = chunk.map((row, rowIndex) => {
                try {
                  if (!Array.isArray(row)) {
                    console.warn(`Row ${startIndex + rowIndex} is not an array, skipping`)
                    return null
                  }

                  const processedRow = row.map((cell) => {
                    if (!cell || typeof cell !== "object") {
                      return String(cell || "")
                    }
                    return String(cell.text || cell.value || "")
                  })

                  return processedRow
                } catch (rowError) {
                  console.warn(`Error processing row ${startIndex + rowIndex}:`, rowError)
                  return null
                }
              }).filter(row => row !== null)

              // Add processed chunk to worksheet
              if (processedChunk.length > 0) {
                const currentRange = XLSX.utils.decode_range(ws["!ref"] || "A1")
                const startRow = currentRange.e.r + 1

                processedChunk.forEach((row, idx) => {
                  row.forEach((cell, colIdx) => {
                    const cellAddress = XLSX.utils.encode_cell({ r: startRow + idx, c: colIdx })
                    ws[cellAddress] = { t: "s", v: String(cell) }
                  })
                })

                // Update worksheet range
                const newEndRow = startRow + processedChunk.length - 1
                ws["!ref"] = XLSX.utils.encode_range({
                  s: { r: 0, c: 0 },
                  e: { r: newEndRow, c: columns.length - 1 }
                })
              }

              processedRows = endIndex

              // Force garbage collection and continue with next chunk
              if (typeof window !== "undefined" && window["gc"]) {
                try {
                  window["gc"]()
                } catch (e) {
                  // Ignore if gc is not available
                }
              }

              // Process next chunk with small delay to prevent blocking
              setTimeout(() => processChunk(endIndex), 50)

            } catch (chunkError) {
              console.error(`Error processing chunk ${startIndex}-${endIndex}:`, chunkError)
              // Continue with next chunk
              setTimeout(() => processChunk(endIndex), 50)
            }
          }

          const finalizeExport = () => {
            try {
              showProgress("Creating Excel file... This may take a moment")

              // Set basic column widths
              ws["!cols"] = columns.map(() => ({ wch: 15 }))

              XLSX.utils.book_append_sheet(wb, ws, name)

              showProgress("Downloading file...")

              // Use writeFile for better memory management with large files
              setTimeout(() => {
                try {
                  XLSX.writeFile(wb, `${index === 0 ? fileName : fileName + "_filtered"}.xlsx`, {
                    bookType: "xlsx",
                    compression: true,
                    bookSST: false // Disable shared string table for memory efficiency
                  })

                  hideProgress()
                  console.log(`Large dataset export completed: ${processedRows} rows`)

                  // Final cleanup
                  ws = null

                } catch (writeError) {
                  hideProgress()
                  console.error("File write error:", writeError)
                  throw new Error("Failed to create Excel file. Dataset may be too large for browser memory.")
                }
              }, 100)

            } catch (finalizeError) {
              hideProgress()
              console.error("Finalization error:", finalizeError)
              throw finalizeError
            }
          }

          // Start processing
          processChunk(0)

        } catch (processingError) {
          hideProgress()
          console.error("Large dataset processing error:", processingError)
          throw processingError
        }
      }, 100)

    } catch (error) {
      console.error("Large dataset export error:", error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      throw new Error(`Memory-efficient export failed: ${errorMessage}`)
    }
  }

  function showLargeDatasetConfirmation(rowCount, fileCount) {
    return new Promise((resolve) => {
      const message = `Dataset is extremely large (${rowCount.toLocaleString()} rows).\n\n` +
        `This will be split into ${fileCount} separate Excel files to prevent memory issues.\n\n` +
        "Continue with split export?"

      // Use a simple confirmation - in a real app, you might want a custom modal
      const result = window.confirm(message)
      resolve(result)
    })
  }

  function handleExtremelyLargeDataset(columns, data, name, index) {
    const maxRowsPerFile = 100000
    const fileCount = Math.ceil(data.length / maxRowsPerFile)

    showLargeDatasetConfirmation(data.length, fileCount).then((proceed) => {
      if (!proceed) return

      for (let fileIndex = 0; fileIndex < fileCount; fileIndex++) {
        const startRow = fileIndex * maxRowsPerFile
        const endRow = Math.min(startRow + maxRowsPerFile, data.length)
        const chunk = data.slice(startRow, endRow)

        const splitFileName = `${index === 0 ? fileName : fileName + "_filtered"}_part${fileIndex + 1}`

        setTimeout(() => {
          // Create Excel for this chunk with custom filename
          try {
            const processedChunk = processDataRows(chunk)
            if (processedChunk.length > 0) {
              const ws = XLSX.utils.aoa_to_sheet([columns, ...processedChunk])
              const wb = { Sheets: { [name]: ws }, SheetNames: [name] }

              XLSX.writeFile(wb, `${splitFileName}.xlsx`, {
                bookType: "xlsx",
                compression: true,
                bookSST: false
              })

              console.log(`Exported part ${fileIndex + 1}/${fileCount}: ${chunk.length} rows`)
            }
          } catch (error) {
            console.error(`Error exporting part ${fileIndex + 1}:`, error)
            const errorMessage = error instanceof Error ? error.message : String(error)
            alert(`Failed to export part ${fileIndex + 1}: ${errorMessage}`)
          }
        }, fileIndex * 1000) // Stagger file creation
      }
    })
  }

  function createProgressDiv() {
    const progressDiv = document.createElement("div")
    progressDiv.id = "excel-export-progress"
    progressDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 20px;
      border-radius: 8px;
      z-index: 10000;
      font-family: Arial, sans-serif;
      text-align: center;
      min-width: 300px;
    `
    document.body.appendChild(progressDiv)
    return progressDiv
  }

  function createAndDownloadExcel(columns, alteredData, name, index) {
    const ws = XLSX.utils.aoa_to_sheet([columns, ...alteredData])
    const wb = { Sheets: { [name]: ws }, SheetNames: [name] }
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" })
    const fileData = new Blob([excelBuffer], { type: "xlsx" })
    FileSaver.saveAs(
      fileData,
      `${index === 0 ? fileName : fileName + "_filtered"}` + ".xlsx"
    )
  }
  const handleClick = (dataset, index) => {
    if (isVulnerabilityTable) {
      handleDownloadExcel(exportData(index), index);
    } else {
      handleDownloadExcel(dataset, index)
    }
  }

  return (
    <CDropdown className="export-btn">
      <CDropdownToggle className="py-0">{children}</CDropdownToggle>
      <CDropdownMenu>
        {Array.isArray(excelDatasets) &&
          excelDatasets.map((dataset, index) => {
            return (
              <button
                className="dropdown-item"
                role="menuitem"
                onClick={() => { handleClick(dataset, index) }}
                key={index}
              >
                {index === 0 ? "Export to Excel" : "Export Filtered Data"}
              </button>
              // <ExcelFile
              //   key={index}
              //   filename={index === 0 ? fileName : fileName + "_filtered"}
              //   element={
              //     <CDropdownItem>
              //       {index === 0 ? "Export to Excel" : "Export Filtered Data"}
              //     </CDropdownItem>
              //   }
              // >
              //   <ExcelSheet
              //     dataSet={[stringfyAllValues(dataset)]}
              //     name={dataset.name}
              //     key={dataset.name}
              //   />
              // </ExcelFile>
            )
          })}
      </CDropdownMenu>
    </CDropdown>
  )
}

MultipleExport.propTypes = {
  children: PropTypes.node,
  excelDatasets: PropTypes.array,
  fileName: PropTypes.string,
  isVulnerabilityTable: PropTypes.bool,
  exportData: PropTypes.func
}